// Market Watch KPI Generator
// Generates KPIs from flattened pivot data (grouped_grid with _level hierarchy)

import { DynamicKPI, EnrichedData } from "./types";
import { KPI_UNIFORM_THEME } from "./constants";
import { KPI_LIMITS } from "../../../shared/constants";

// Helper to format numbers with appropriate precision
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 10000) return `${(num / 1000).toFixed(1)}K`;
  if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
  if (num >= 100) return Math.round(num).toLocaleString();
  if (num >= 10) return num.toFixed(1);
  if (num >= 1) return num.toFixed(2);
  if (num > 0) return num.toFixed(4); // Very small numbers
  return "0";
};

// Helper to format percentage
// Note: Backend provides "Demand Share %" as actual percentage values (e.g., 0.5 means 0.5%, not 50%)
const formatPercent = (num: number, isAlreadyPercent: boolean = false): string => {
  if (isAlreadyPercent || num < 1) {
    // Value is already a percentage (e.g., 0.5 means 0.5%)
    // Show with appropriate precision
    if (num < 0.01) {
      return `${num.toFixed(4)}%`; // Very small: show 4 decimals
    } else if (num < 1) {
      return `${num.toFixed(2)}%`; // Small: show 2 decimals
    } else if (num < 10) {
      return `${num.toFixed(1)}%`; // Medium: show 1 decimal
    }
    return `${Math.round(num)}%`; // Large: no decimals
  }
  // Value is a decimal ratio (e.g., 0.5 means 50%) - convert to percentage
  return `${(num * 100).toFixed(1)}%`;
};

// Helper to create a clean display name from column header
const cleanColumnName = (name: string): string => {
  return name
    .replace(/^(sum|avg|count|total|mean|max|min)_/gi, "")
    .replace(/_(sum|avg|count|total|mean|max|min)$/gi, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase())
    .trim()
    .substring(0, KPI_LIMITS.MAX_METRIC_NAME_WIDTH);
};

// Determine the best icon for a metric
const getMetricIcon = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes("price")) return "💰";
  if (lower.includes("rating")) return "⭐";
  if (lower.includes("review")) return "💬";
  if (lower.includes("volume") || lower.includes("demand")) return "📊";
  if (lower.includes("share") || lower.includes("percent")) return "📈";
  if (lower.includes("forecast") || lower.includes("purchase")) return "🛒";
  if (lower.includes("impression") || lower.includes("click")) return "👁️";
  if (lower.includes("count") || lower.includes("num")) return "🔢";
  return "📊";
};

// Main: Generate KPIs based on flattened pivot data (grouped_grid)
export const generateDynamicKPIs = (
  enrichedData: EnrichedData,
  _htmlReport: string,
  _summary: string
): DynamicKPI[] => {
  const headers = enrichedData.headers;
  const headersLower = headers.map(h => (h || "").toLowerCase());
  const rows = enrichedData.rows;
  const kpis: DynamicKPI[] = [];

  if (rows.length === 0) {
    console.log("No rows in flattened pivot data, skipping KPIs");
    return [];
  }

  const getUniformTheme = () => KPI_UNIFORM_THEME;

  // Check if we have _level column (hierarchical grouped_grid)
  const levelIdx = headersLower.indexOf("_level");

  // Get the summary row - could be level 0 (grand total) or level 1 (ASIN subtotal for single ASIN)
  let summaryRow: any[] | null = null;
  if (levelIdx >= 0) {
    // First try to find level 0 (grand total across all ASINs)
    summaryRow = rows.find(r => r[levelIdx] === 0 || r[levelIdx] === "0");

    // If no level 0, use level 1 (ASIN subtotal - common for single ASIN selection)
    if (!summaryRow) {
      summaryRow = rows.find(r => r[levelIdx] === 1 || r[levelIdx] === "1");
    }
  }

  // If still no summary row, use first row
  const sourceRow = summaryRow || rows[0] || null;

  console.log("Using source row for KPIs:", sourceRow ? `level ${sourceRow[levelIdx]}` : "none");

  console.log("KPI Analysis:", {
    hasLevelColumn: levelIdx >= 0,
    hasSummaryRow: !!summaryRow,
    summaryRowLevel: summaryRow ? summaryRow[levelIdx] : null,
    totalRows: rows.length,
    headers: headers.slice(0, 10)
  });

  // Analyze all columns for their types and values
  const columnAnalysis: Array<{
    idx: number;
    name: string;
    type: "sum" | "avg" | "count" | "percent" | "other";
    grandTotalValue: number;
    totalSum: number;
    avgValue: number;
  }> = [];

  for (let i = 0; i < headers.length; i++) {
    if (headers[i].startsWith("_")) continue; // Skip internal fields

    const headerLower = headersLower[i];

    // Check if this column has numeric values
    const sampleValues = rows.slice(0, 10).map(r => parseFloat(r[i])).filter(v => !isNaN(v) && isFinite(v));
    const isNumeric = sampleValues.length >= rows.slice(0, 10).length * 0.5;

    if (!isNumeric) continue; // Skip non-numeric columns for KPI analysis

    // Determine column type based on keywords in column name
    // Priority: explicit suffixes > keywords > default to sum for numeric
    let type: "sum" | "avg" | "count" | "percent" | "other" = "other";

    // Check for explicit aggregation suffixes first
    if (headerLower.includes("_sum") || headerLower.includes("sum_")) {
      type = "sum";
    } else if (headerLower.includes("_avg") || headerLower.includes("avg_") || headerLower.includes("average") || headerLower.includes("mean")) {
      type = "avg";
    } else if (headerLower.includes("_count") || headerLower.includes("count_") || headerLower.endsWith("_count")) {
      type = "count";
    } else if (headerLower.includes("share") || headerLower.includes("percent") || headerLower.includes("%")) {
      type = "percent";
    }
    // Check for keyword-based type detection (for columns without suffixes)
    else if (headerLower.includes("volume") || headerLower.includes("forecast") ||
             headerLower.includes("demand") || headerLower.includes("purchase") ||
             headerLower.includes("sales") || headerLower.includes("revenue") ||
             headerLower.includes("units") || headerLower.includes("quantity") ||
             headerLower.includes("total") || headerLower.includes("amount")) {
      type = "sum"; // Volume/quantity metrics are typically summed
    } else if (headerLower.includes("price") || headerLower.includes("cost") ||
               headerLower.includes("rating") || headerLower.includes("score") ||
               headerLower.includes("rate") || headerLower.includes("margin")) {
      type = "avg"; // Price/rating metrics are typically averaged
    } else if (headerLower.includes("count") || headerLower.includes("num") ||
               headerLower.includes("number")) {
      type = "count";
    } else {
      // Default: treat as sum for large values, avg for small values
      const maxVal = Math.max(...sampleValues);
      type = maxVal > 100 ? "sum" : "avg";
    }

    // Get grand total value if available
    const grandTotalValue = sourceRow ? parseFloat(sourceRow[i]) || 0 : 0;

    // Calculate sum and avg from all non-subtotal rows
    const detailRows = levelIdx >= 0
      ? rows.filter(r => {
          const level = parseInt(r[levelIdx]);
          return !isNaN(level) && level > 0;
        })
      : rows;

    const values = detailRows.map(r => parseFloat(r[i])).filter(v => !isNaN(v) && isFinite(v));
    const totalSum = values.reduce((a, b) => a + b, 0);
    const avgValue = values.length > 0 ? totalSum / values.length : 0;

    if (grandTotalValue > 0 || totalSum > 0 || avgValue > 0) {
      columnAnalysis.push({
        idx: i,
        name: headers[i],
        type,
        grandTotalValue,
        totalSum,
        avgValue
      });
    }
  }

  console.log("=== KPI COLUMN ANALYSIS ===");
  console.log("Total columns analyzed:", columnAnalysis.length);
  console.log("Column details:", columnAnalysis.map(c => ({
    name: c.name,
    type: c.type,
    grandTotalValue: c.grandTotalValue,
    totalSum: c.totalSum,
    avgValue: c.avgValue
  })));

  // Track used columns to avoid duplicates
  const usedColumns = new Set<number>();

  // Count unique categories for more meaningful KPIs
  const countUniqueInColumn = (colName: string): number => {
    const colIdx = headers.findIndex(h => h.toLowerCase() === colName.toLowerCase());
    if (colIdx === -1) return 0;
    const detailRows = levelIdx >= 0
      ? rows.filter(r => parseInt(r[levelIdx]) > 0)
      : rows;
    const uniqueValues = new Set(
      detailRows.map(r => r[colIdx]).filter(v => v != null && v !== "" && v !== "Grand Total")
    );
    return uniqueValues.size;
  };

  // Find categorical columns for unique counts
  const priceClustersCount = countUniqueInColumn("global_price_cluster_label");
  const featureClustersCount = countUniqueInColumn("global_cluster_feature_label");
  const asinsCount = countUniqueInColumn("target_asin");

  // 1. Total Segments KPI (more meaningful than just "records")
  const nonTotalRows = levelIdx >= 0
    ? rows.filter(r => parseInt(r[levelIdx]) > 0).length
    : rows.length;

  // Find the max level to determine what detail rows represent
  const maxLevel = levelIdx >= 0 ? Math.max(...rows.map(r => parseInt(r[levelIdx]) || 0)) : 0;
  const detailRowCount = levelIdx >= 0
    ? rows.filter(r => parseInt(r[levelIdx]) === maxLevel).length
    : rows.length;

  kpis.push({
    id: "total_segments",
    label: "📊 MARKET SEGMENTS",
    value: detailRowCount.toLocaleString(),
    icon: "📊",
    colorTheme: getUniformTheme(),
    priority: 10,
  });

  // Add price clusters KPI if available
  if (priceClustersCount > 0) {
    kpis.push({
      id: "price_clusters",
      label: "💰 PRICE CLUSTERS",
      value: priceClustersCount.toLocaleString(),
      icon: "💰",
      colorTheme: getUniformTheme(),
      priority: 6,
    });
  }

  // Add feature clusters KPI if available
  if (featureClustersCount > 0) {
    kpis.push({
      id: "feature_clusters",
      label: "🏷️ FEATURE CLUSTERS",
      value: featureClustersCount.toLocaleString(),
      icon: "🏷️",
      colorTheme: getUniformTheme(),
      priority: 5,
    });
  }

  // 2. Find volume/demand/forecast SUM column (primary metric)
  const volumeCol = columnAnalysis.find(c =>
    c.type === "sum" &&
    (c.name.toLowerCase().includes("volume") ||
     c.name.toLowerCase().includes("purchase") ||
     c.name.toLowerCase().includes("demand") ||
     c.name.toLowerCase().includes("forecast"))
  );

  if (volumeCol) {
    usedColumns.add(volumeCol.idx);
    const value = volumeCol.grandTotalValue > 0 ? volumeCol.grandTotalValue : volumeCol.totalSum;
    kpis.push({
      id: "total_volume",
      label: `${getMetricIcon(volumeCol.name)} ${cleanColumnName(volumeCol.name).toUpperCase()}`,
      value: formatNumber(value),
      icon: getMetricIcon(volumeCol.name),
      colorTheme: getUniformTheme(),
      priority: 9,
    });
  }

  // 3. Find a share/percentage column
  const shareCol = columnAnalysis.find(c =>
    c.type === "percent" && !usedColumns.has(c.idx)
  );

  if (shareCol) {
    usedColumns.add(shareCol.idx);
    const value = shareCol.grandTotalValue > 0 ? shareCol.grandTotalValue : shareCol.avgValue;
    // Check if column name contains "%" - indicates value is already a percentage
    const isAlreadyPercent = shareCol.name.includes("%") || shareCol.name.toLowerCase().includes("share");
    kpis.push({
      id: "share_metric",
      label: `📈 ${cleanColumnName(shareCol.name).toUpperCase()}`,
      value: formatPercent(value, isAlreadyPercent),
      icon: "📈",
      colorTheme: getUniformTheme(),
      priority: 8,
    });
  }

  // 4. Find price AVG column
  const priceCol = columnAnalysis.find(c =>
    (c.type === "avg" || c.type === "other") &&
    c.name.toLowerCase().includes("price") &&
    !usedColumns.has(c.idx)
  );

  if (priceCol) {
    usedColumns.add(priceCol.idx);
    const value = priceCol.grandTotalValue > 0 ? priceCol.grandTotalValue : priceCol.avgValue;
    kpis.push({
      id: "avg_price",
      label: "💰 AVG PRICE",
      value: `$${value.toFixed(2)}`,
      icon: "💰",
      colorTheme: getUniformTheme(),
      priority: 7,
    });
  }

  // 5. Find rating AVG column
  const ratingCol = columnAnalysis.find(c =>
    c.name.toLowerCase().includes("rating") && !usedColumns.has(c.idx)
  );

  if (ratingCol) {
    usedColumns.add(ratingCol.idx);
    const value = ratingCol.grandTotalValue > 0 ? ratingCol.grandTotalValue : ratingCol.avgValue;
    kpis.push({
      id: "avg_rating",
      label: "⭐ AVG RATING",
      value: value.toFixed(2),
      icon: "⭐",
      colorTheme: getUniformTheme(),
      priority: 6,
    });
  }

  // 6. Find COUNT column
  const countCol = columnAnalysis.find(c =>
    c.type === "count" && !usedColumns.has(c.idx)
  );

  if (countCol) {
    usedColumns.add(countCol.idx);
    const value = countCol.grandTotalValue > 0 ? countCol.grandTotalValue : countCol.totalSum;
    kpis.push({
      id: "count_metric",
      label: `🔢 ${cleanColumnName(countCol.name).toUpperCase()}`,
      value: formatNumber(value),
      icon: "🔢",
      colorTheme: getUniformTheme(),
      priority: 5,
    });
  }

  // 7. Find additional SUM columns (impressions, clicks, etc.)
  const additionalSumCols = columnAnalysis.filter(c =>
    c.type === "sum" && !usedColumns.has(c.idx)
  );

  for (const col of additionalSumCols) {
    if (kpis.length >= KPI_LIMITS.MAX_KPIS) break;
    usedColumns.add(col.idx);
    const value = col.grandTotalValue > 0 ? col.grandTotalValue : col.totalSum;
    if (value > 0) {
      kpis.push({
        id: `sum_${col.idx}`,
        label: `${getMetricIcon(col.name)} ${cleanColumnName(col.name).toUpperCase()}`,
        value: formatNumber(value),
        icon: getMetricIcon(col.name),
        colorTheme: getUniformTheme(),
        priority: 4,
      });
    }
  }

  // 8. Find additional AVG columns
  const additionalAvgCols = columnAnalysis.filter(c =>
    c.type === "avg" && !usedColumns.has(c.idx)
  );

  for (const col of additionalAvgCols) {
    if (kpis.length >= KPI_LIMITS.MAX_KPIS) break;
    usedColumns.add(col.idx);
    const value = col.grandTotalValue > 0 ? col.grandTotalValue : col.avgValue;
    if (value > 0) {
      const isPrice = col.name.toLowerCase().includes("price");
      kpis.push({
        id: `avg_${col.idx}`,
        label: `${getMetricIcon(col.name)} ${cleanColumnName(col.name).toUpperCase()}`,
        value: isPrice ? `$${value.toFixed(2)}` : value.toFixed(2),
        icon: getMetricIcon(col.name),
        colorTheme: getUniformTheme(),
        priority: 3,
      });
    }
  }

  // Sort by priority and return top KPIs
  const result = kpis.sort((a, b) => b.priority - a.priority).slice(0, KPI_LIMITS.MAX_KPIS);

  console.log("Generated KPIs:", result.map(k => k.label));

  return result;
};
